/**
 * Decoded images, kept ready for the next frame.
 *
 * ## Why a cache is not optional here
 *
 * Painting is synchronous and runs inside one animation frame; decoding an image is
 * asynchronous and takes milliseconds to tens of milliseconds. Those cannot be reconciled by
 * waiting. So the painter asks for a decoded image, gets one or gets nothing, and the cache
 * arranges for a repaint once the decode finishes. An image appears on the frame after the one
 * that first asked for it, and on every frame after that at no cost.
 *
 * Without the cache, each frame would start a fresh decode of every image — sixty decodes a
 * second of the same bytes, none of them finishing in time to be drawn.
 *
 * ## Why the key is the whole `href`
 *
 * Images are stored as `data:` URIs, so the href *is* the bytes. Two entities showing the same
 * picture share one decode, and an href that changes is a different picture by definition.
 * The cost is holding the string twice; the alternative — hashing — buys nothing, because the
 * comparison a `Map` does is already the fast path for identical string references.
 *
 * ## Failure is remembered, not retried
 *
 * A broken data URI fails to decode every time. Recording the failure stops the frame loop
 * from re-attempting it forever, which would otherwise be a permanent background cost with
 * nothing to show for it.
 */

export type DecodedImage = CanvasImageSource & { readonly width: number; readonly height: number };

interface Entry {
  readonly state: 'loading' | 'ready' | 'failed';
  readonly image: DecodedImage | null;
}

export class ImageCache {
  private readonly entries = new Map<string, Entry>();
  private readonly onReady: () => void;
  private readonly create: () => HTMLImageElement;

  /**
   * @param onReady Called when a decode finishes, so the caller can schedule a frame.
   * @param create  Injected so tests need no DOM. Defaults to a real `Image`.
   */
  constructor(onReady: () => void, create?: () => HTMLImageElement) {
    this.onReady = onReady;
    this.create = create ?? (() => new globalThis.Image());
  }

  /**
   * The decoded image for `href`, or `null` while it is not available.
   *
   * Starts a decode on the first ask. Returning `null` rather than blocking is what keeps the
   * frame budget intact — the frame that asks first simply draws without it.
   */
  get(href: string): DecodedImage | null {
    const existing = this.entries.get(href);
    if (existing !== undefined) return existing.image;

    this.entries.set(href, { state: 'loading', image: null });

    const image = this.create();
    image.addEventListener('load', () => {
      this.entries.set(href, { state: 'ready', image });
      this.onReady();
    });
    image.addEventListener('error', () => {
      // Remembered, not retried: a broken URI fails identically every time, and retrying it
      // once a frame is a permanent cost with nothing to show for it.
      this.entries.set(href, { state: 'failed', image: null });
    });
    image.src = href;

    return null;
  }

  /** Whether `href` has been tried and cannot be decoded. Used to report a broken import. */
  hasFailed(href: string): boolean {
    return this.entries.get(href)?.state === 'failed';
  }

  clear(): void {
    this.entries.clear();
  }
}
